(() => {
    if (window.FloatingWidgetIntegrations?.initialized) return;

    const SIENA_TIMEOUT_MS = 8000;
    const SIENA_POLL_INTERVAL_MS = 150;
    const SIENA_RESTART_MODAL_ID = 'support-chat-restart';
    const SIENA_RESTART_DELAY_MS = 600;
    const RECART_ROOT_ID = 'recart-popup-root';
    const RECART_MODULE_LOADED_EVENT = 'recart:optin-tool:popup-module-loaded';
    const RECART_STICKY_STYLE_ID = 'tao-recart-sticky-trigger-style';
    const SESSION_STORAGE_KEYS = {
        activeHash: 'supportChatActiveHash',
    };
    const SIENA_STORAGE_KEYS = [
        'siena_user_id',
        'siena_user_id_timestamp',
        'siena_persistent_trace_id',
        'siena_persistent_trace_id_timestamp',
    ];

    const ACTIONS = {
        restartConfirm: 'support-chat:restart-confirm',
    };

    let pendingRestartHash = '';

    const CHAT_HASH_CONFIG = {
        '#order-status': {
            message: 'Check order status',
        },
        '#returns': {
            message: 'I need help with a return / exchange',
        },
        '#support': {
            message: '',
        },
    };

    function init() {
        document.addEventListener('click', handleDocumentClick);
        handleInitialHash();
        initRecartStickyTrigger();
    }

    function initRecartStickyTrigger() {
        if (!document.body.matches('.tao-product-new, .tao-product-legacy')) return;

        if (!injectRecartStickyTriggerStyle()) {
            window.addEventListener(
                RECART_MODULE_LOADED_EVENT,
                handleRecartPopupModuleLoaded,
                { once: true }
            );
        }
    }

    function injectRecartStickyTriggerStyle() {
        const root = document.getElementById(RECART_ROOT_ID);
        if (!(root instanceof HTMLElement) || !root.shadowRoot) return false;

        if (root.shadowRoot.querySelector(`#${RECART_STICKY_STYLE_ID}`)) return true;

        const style = document.createElement('style');
        style.id = RECART_STICKY_STYLE_ID;
        style.textContent = `
            :host([data-tao-popup-dismissed]) [role='dialog'],
            :host([data-tao-popup-dismissed]) [data-testid^='popup-overlay-'] {
                visibility: hidden !important;
                pointer-events: none !important;
            }

            @media (max-width: 749px) {
                [data-testid^='popup-minimized-'] {
                    translate: 0 calc(0px - var(--tao-recart-sticky-offset, 0px)) !important;
                    transition: translate .35s ease !important;
                }
            }
        `;
        root.shadowRoot.appendChild(style);
        root.shadowRoot.addEventListener('click', handleRecartDismissClick, true);

        return true;
    }

    /** Hide both layers before Recart's asynchronous dismissal can leave an empty panel. */
    function handleRecartDismissClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return;

        const button = target.closest('button');
        if (!button) return;

        const root = event.currentTarget.host;
        const minimized = button.closest('[data-testid^="popup-minimized-"]');
        if (minimized) {
            if (button.getAttribute('aria-label') !== 'Close minimized popup') {
                root.removeAttribute('data-tao-popup-dismissed');
            }
            return;
        }

        const isClose = button.getAttribute('aria-label') === 'Close popup';
        const isTeaserDecline = button.closest('[class*="-custom-teaser-content"]')
            && /^no,?\s+thanks$/i.test(button.textContent.trim());
        if (isClose || isTeaserDecline) {
            root.setAttribute('data-tao-popup-dismissed', '');
        }
        // Keep the app's click handler and DOM intact so it owns its dismissal state.
    }

    function handleRecartPopupModuleLoaded() {
        injectRecartStickyTriggerStyle();
    }

    /**
     * @param {MouseEvent} event
     */
    function handleDocumentClick(event) {
        if (event.defaultPrevented) return;

        if (handleRestartConfirmClick(event)) return;

        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        const target = event.target;
        if (!(target instanceof Element)) return;

        const link = target.closest('a[href]');
        if (!(link instanceof HTMLAnchorElement)) return;
        if (link.target && link.target !== '_self') return;
        if (link.hasAttribute('download')) return;
        if (!isSamePageLink(link)) return;

        const hash = normalizeHash(link.hash);
        if (!checkIsChatHash(hash)) return;
        if (checkHasMatchingSection(hash)) return;

        event.preventDefault();
        openChatForHash(hash);
    }

    function handleInitialHash() {
        const hash = normalizeHash(window.location.hash);
        if (!checkIsChatHash(hash)) return;
        if (checkHasMatchingSection(hash)) return;

        openChatForHash(hash);
    }

    /**
     * @param {HTMLAnchorElement} link
     * @returns {boolean}
     */
    function isSamePageLink(link) {
        const url = new URL(link.href, window.location.href);
        const current = new URL(window.location.href);

        return (
            url.origin === current.origin &&
            url.pathname === current.pathname &&
            url.search === current.search
        );
    }

    /**
     * @param {string} hash
     * @returns {boolean}
     */
    function checkIsChatHash(hash) {
        return Object.prototype.hasOwnProperty.call(CHAT_HASH_CONFIG, hash);
    }

    /**
     * @param {string} hash
     * @returns {boolean}
     */
    function checkHasMatchingSection(hash) {
        const id = hash.replace(/^#/, '');
        if (!id) return false;

        return Boolean(document.getElementById(id));
    }

    /**
     * @param {string} hash
     */
    function openChatForHash(hash) {
        const config = CHAT_HASH_CONFIG[hash];
        if (!config) return;

        if (checkShouldConfirmRestart(hash)) {
            pendingRestartHash = hash;
            openRestartModal();
            return;
        }

        launchChatForHash(hash);
    }

    /**
     * @param {string} hash
     */
    function launchChatForHash(hash) {
        const config = CHAT_HASH_CONFIG[hash];
        if (!config) return;

        waitForSienaChatReady()
            .then((api) => {
                if (!api) return;

                api.open(config.message || '');
                setActiveChatHash(hash);
            })
            .catch(() => {});
    }

    /**
     * @param {string} hash
     * @returns {boolean}
     */
    function checkShouldConfirmRestart(hash) {
        if (!checkIsSienaChatOpen()) return false;

        const activeHash = getActiveChatHash();
        if (activeHash && activeHash === hash) return false;

        return true;
    }

    /**
     * @param {MouseEvent} event
     * @returns {boolean}
     */
    function handleRestartConfirmClick(event) {
        const target = event.target;
        if (!(target instanceof Element)) return false;

        const trigger = target.closest(`[data-action="${ACTIONS.restartConfirm}"]`);
        if (!(trigger instanceof HTMLElement)) return false;

        event.preventDefault();

        const hash = pendingRestartHash;
        pendingRestartHash = '';

        closeRestartModal();
        resetSienaChat();

        if (checkIsChatHash(hash)) {
            window.setTimeout(() => {
                launchChatForHash(hash);
            }, SIENA_RESTART_DELAY_MS);
        }

        return true;
    }

    function waitForSienaChatReady() {
        return new Promise((resolve) => {
            const startedAt = Date.now();

            function check() {
                const api = getSienaChatApi();

                if (api?.ready()) {
                    resolve(api);
                    return;
                }

                if (Date.now() - startedAt >= SIENA_TIMEOUT_MS) {
                    resolve(api);
                    return;
                }

                window.setTimeout(check, SIENA_POLL_INTERVAL_MS);
            }

            check();
        });
    }

    function getSienaChatApi() {
        const iframe = getSienaChatIframe();

        if (iframe?.contentWindow) {
            return {
                open: openSienaChatIframe,
                ready: () => true,
            };
        }

        if (typeof window.SienaLaunchChat === 'function') {
            return {
                open: window.SienaLaunchChat,
                ready: () => Boolean(getSienaChatIframe()),
            };
        }

        if (window.SienaLiveChatWidget && typeof window.SienaLiveChatWidget.launch === 'function') {
            return {
                open: (message) => window.SienaLiveChatWidget.launch(message || ''),
                ready: () => {
                    if (typeof window.SienaLiveChatWidget.isReady === 'function') {
                        return window.SienaLiveChatWidget.isReady();
                    }

                    return Boolean(getSienaChatIframe());
                },
            };
        }

        return null;
    }

    function openRestartModal() {
        const modal = getRestartModal();
        if (modal && typeof modal.openModal === 'function') {
            modal.openModal({
                updateUrl: false,
            });
            return;
        }

        if (window.customElements && !customElements.get('custom-modal')) {
            customElements.whenDefined('custom-modal').then(openRestartModal).catch(() => {});
        }
    }

    function closeRestartModal() {
        const modal = getRestartModal();
        if (modal && typeof modal.closeModal === 'function') {
            modal.closeModal({
                restoreFocus: false,
                clearUrl: false,
            });
        }
    }

    /**
     * @returns {HTMLElement | null}
     */
    function getRestartModal() {
        const modal = document.querySelector(
            `custom-modal[data-modal-id="${SIENA_RESTART_MODAL_ID}"]`
        );

        return modal instanceof HTMLElement ? modal : null;
    }

    /**
     * @param {string} message
     */
    function openSienaChatIframe(message) {
        const iframe = getSienaChatIframe();
        if (!iframe?.contentWindow) return;

        iframe.contentWindow.postMessage({ action: 'openWidget' }, '*');

        if (message) {
            iframe.contentWindow.postMessage({ action: 'sendMessage', message }, '*');
        }
    }

    function resetSienaChat() {
        const iframe = getSienaChatIframe();
        const resetMessage = {
            action: 'resetConversation',
            source: 'support_chat_trigger',
            userInitiated: true,
            reason: 'start_new_trigger_chat',
            clearUserContext: true,
            clearPersistedStorage: true,
        };

        clearSienaPersistedChatState();

        const widget = getSienaLiveChatWidgetInstance();
        if (widget && typeof widget.handleChatClear === 'function') {
            widget.handleChatClear(resetMessage);
        }

        window.postMessage(resetMessage, window.location.origin);

        if (iframe?.contentWindow) {
            iframe.contentWindow.postMessage({ action: 'closeWidget' }, '*');
            iframe.contentWindow.postMessage(resetMessage, '*');
        }

        if (window.SienaLiveChatWidget && typeof window.SienaLiveChatWidget.close === 'function') {
            window.SienaLiveChatWidget.close();
        }

        setActiveChatHash('');
    }

    /**
     * @returns {Record<string, unknown> | null}
     */
    function getSienaLiveChatWidgetInstance() {
        if (
            window.SienaLiveChatWidget &&
            typeof window.SienaLiveChatWidget.getInstance === 'function'
        ) {
            const widget = window.SienaLiveChatWidget.getInstance();
            return widget && typeof widget === 'object' ? widget : null;
        }

        return null;
    }

    function clearSienaPersistedChatState() {
        try {
            SIENA_STORAGE_KEYS.forEach((key) => window.localStorage.removeItem(key));
        } catch {}
    }

    /**
     * @returns {boolean}
     */
    function checkIsSienaChatOpen() {
        const iframe = getSienaChatIframe();
        if (!iframe) return false;

        if (window.SienaLiveChatWidget?.state === 'open') return true;

        return (
            iframe.classList.contains('siena-iframe--bottom-0') ||
            iframe.classList.contains('siena-iframe--fullscreen')
        );
    }

    /**
     * @returns {HTMLIFrameElement | null}
     */
    function getSienaChatIframe() {
        const iframe =
            document.getElementById('SIENA_CHAT_IFRAME') ||
            document.querySelector('iframe.siena-iframe');

        return iframe instanceof HTMLIFrameElement ? iframe : null;
    }

    /**
     * @returns {string}
     */
    function getActiveChatHash() {
        try {
            return normalizeHash(window.sessionStorage.getItem(SESSION_STORAGE_KEYS.activeHash));
        } catch {
            return '';
        }
    }

    /**
     * @param {string} hash
     */
    function setActiveChatHash(hash) {
        try {
            if (!hash) {
                window.sessionStorage.removeItem(SESSION_STORAGE_KEYS.activeHash);
                return;
            }

            window.sessionStorage.setItem(SESSION_STORAGE_KEYS.activeHash, normalizeHash(hash));
        } catch {}
    }

    /**
     * @param {string} hash
     * @returns {string}
     */
    function normalizeHash(hash) {
        return String(hash || '').trim().toLowerCase();
    }

    window.FloatingWidgetIntegrations = {
        initialized: true,
    };

    init();
})();
